import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@vinoga/db";
import { GripVertical, Pencil, Pin, Plus, Sparkles, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { mediaSrc } from "@/lib/media";

type CollectionRow = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  image_url: string | null;
  image_side: "left" | "right";
  sort_order: number;
  is_active: boolean;
  is_homepage_pinned: boolean;
  date_from: string | null;
  date_to: string | null;
};

export function Collections() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const tr = useT();
  const [rows, setRows] = useState<CollectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("collections")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) {
      toast({
        title: tr("collections.errorLoading", "Failed to load collections"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      setRows((data ?? []) as CollectionRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id: string) => {
    if (
      !confirm(
        tr("collections.confirmDelete", "Delete this collection? Products stay; only the collection is removed.")
      )
    )
      return;
    const { error } = await supabase.from("collections").delete().eq("id", id);
    if (error) {
      toast({ title: tr("common.deleteFailed", "Delete failed"), variant: "destructive" });
      return;
    }
    refresh();
  };

  const togglePin = async (c: CollectionRow) => {
    const next = !c.is_homepage_pinned;
    const { error } = await supabase
      .from("collections")
      .update({ is_homepage_pinned: next })
      .eq("id", c.id);
    if (error) {
      toast({ title: tr("common.saveFailed", "Save failed"), variant: "destructive" });
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.id === c.id ? { ...r, is_homepage_pinned: next } : r))
    );
  };

  const handleDragStart = (i: number) => setDragIndex(i);
  const handleDragOver = useCallback(
    (e: React.DragEvent, over: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === over) return;
      const next = [...rows];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(over, 0, moved!);
      setRows(next);
      setDragIndex(over);
    },
    [dragIndex, rows]
  );
  const handleDragEnd = async () => {
    setDragIndex(null);
    await Promise.all(
      rows.map((c, idx) =>
        supabase.from("collections").update({ sort_order: idx }).eq("id", c.id)
      )
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-xl font-bold text-foreground">
          {tr("collections.title", "Collections")}
        </h1>
        <Button size="sm" onClick={() => navigate("/collections/new")}>
          <Plus size={16} /> {tr("collections.add", "Add collection")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        {tr(
          "collections.subtitle",
          "Curated product bundles with cover art. Pinned ones surface on the homepage as side-image + 3×2 grid cards."
        )}
      </p>

      {loading ? (
        <div className="p-4 font-body text-sm text-muted-foreground">
          {tr("common.loading", "Loading…")}
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <Sparkles size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-body text-sm">
            {tr("collections.empty", "No collections yet.")}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {rows.map((c, idx) => (
            <div
              key={c.id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              className={`flex cursor-grab items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 active:cursor-grabbing ${
                dragIndex === idx ? "bg-muted/50" : "hover:bg-muted/20"
              }`}
            >
              <GripVertical size={16} className="shrink-0 text-muted-foreground/40" />
              <div className="h-12 w-20 shrink-0 overflow-hidden rounded border border-border bg-muted/20">
                {c.image_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={mediaSrc(c.image_url) ?? undefined} alt={c.name} className="h-full w-full object-cover" />
                ) : (
                  <Sparkles className="h-full w-full p-3 text-muted-foreground/40" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{c.name}</p>
                <p className="font-mono text-xs text-muted-foreground">{c.slug}</p>
              </div>
              <Badge variant={c.is_active ? "default" : "secondary"} className="shrink-0">
                {c.is_active
                  ? tr("collections.statusActive", "active")
                  : tr("collections.statusInactive", "inactive")}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                className={`h-7 w-7 ${c.is_homepage_pinned ? "text-primary" : "text-muted-foreground"}`}
                title={tr("collections.pinHint", "Pin to homepage")}
                onClick={() => togglePin(c)}
              >
                <Pin size={14} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => navigate(`/collections/${c.id}`)}
              >
                <Pencil size={14} className="text-muted-foreground" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => remove(c.id)}
              >
                <Trash2 size={14} className="text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
